import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, Table, Group, Menu } from '@mantine/core';
import Header from '../../../components/util/header';
import ShowEquipment from '../../../components/show-equipment';
import { useSelector } from 'react-redux';
import { fetchHelpdeskEntitiesList, IHelpdeskEntity } from '../../../../libraries/helpdesk/entities';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import PaginationComponent from '../../../components/pagination';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import './styles.scss';

const HelpdeskEntities: React.FC = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const controller = useRef(new AbortController()).current;
  const navigate = useNavigate();

  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [activeRow] = useState<number>(0);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: -4,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [list, setList] = useState<IHelpdeskEntity[]>([]);
  const [newEnt, setNewEnt] = useState<boolean>(false);

  const [menuOpened, setMenuOpened] = useState(false);
  const [menuCoords, setMenuCoords] = useState<{ x: number; y: number } | null>(null);
  const [contextEntityId, setContextEntityId] = useState<number | null>(null);
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadEntitiesList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadEntitiesList = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setList([]);
    setTotalRows(0);

    fetchHelpdeskEntitiesList(
      controller,
      offset,
      limit,
      true,
      filterBarData?.type,
      filterBarData?.entity,
      filterBarData?.name,
      filterBarData?.nif
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.entities || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const detectTypeName = (type: number | undefined) => {
    switch (type) {
      case 0:
        return lang.ENTITY_TYPE_0;
      case 1:
        return lang.ENTITY_TYPE_1;
      case 2:
        return lang.ENTITY_TYPE_2;
      case 3:
        return lang.ENTITY_TYPE_3;
      default:
        return lang.ENTITY_TYPE_NULL;
    }
  };

  const toggleNewEntity = () => {
    setNewEnt(!newEnt);
    navigate(`/helpdesk/entity/new/0`);
  };

  const handleRowRightClick = (event: React.MouseEvent, selectedContract: number) => {
    event.preventDefault();
    setContextEntityId(selectedContract);

    const menuWidth = 200;
    const menuHeight = 300;
    const maxX = window.innerWidth - menuWidth;
    const maxY = window.innerHeight - menuHeight;

    const x = event.clientX > maxX ? maxX : event.clientX;
    const y = event.clientY > maxY ? maxY : event.clientY;

    setMenuCoords({ x, y });
    setMenuOpened(true);
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  return (
    <div className="helpdesk-entities">
      {openDetails && activeRow && lang && (
        <ShowEquipment eid={activeRow} openDetails={openDetails} hideBtn={() => setOpenDetails(false)} lang={lang} />
      )}

      <Header pageTitle={lang.GLOBAL_ENTITIES} />

      <HelpdeskFilterBar
        onSearch={loadEntitiesList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          entity: true,
          name: true,
          nif: true,
          type: true
        }}
        addButton={toggleNewEntity}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.GLOBAL_GROUP}</Table.Th>
              <Table.Th>{lang.GLOBAL_ENTITY}</Table.Th>
              <Table.Th>{lang.GLOBAL_NAME}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_DESCRIPTION}</Table.Th>
              <Table.Th>{lang.GLOBAL_TYPE}</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : !list.length ? (
              <Table.Tr>
                <Table.Td colSpan={5} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              list?.map((entity, index) => (
                <Table.Tr
                  key={index}
                  onContextMenu={event => handleRowRightClick(event, entity?.entid || 0)}
                  style={{ cursor: 'context-menu' }}
                >
                  <Table.Td>{entity?.gid}</Table.Td>
                  <Table.Td>{entity?.entid}</Table.Td>
                  <Table.Td>{entity?.name}</Table.Td>
                  <Table.Td>{entity.description}</Table.Td>
                  <Table.Td>{detectTypeName(entity.type)}</Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {menuOpened && menuCoords && (
        <Menu
          opened={true}
          onClose={() => setMenuOpened(false)}
          styles={{
            dropdown: {
              position: 'absolute',
              top: `${menuCoords.y}px`,
              left: `${menuCoords.x}px`
            }
          }}
        >
          <Menu.Target>
            <div
              style={{
                position: 'absolute',
                top: `${menuCoords.y}px`,
                left: `${menuCoords.x}px`,
                width: 1,
                height: 1
              }}
            />
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={() => navigate(`/helpdesk/entity/read/${contextEntityId}`)}>
              {lang.EDIT_ENTITY_LABEL_READ}
            </Menu.Item>
            <Menu.Item onClick={() => navigate(`/helpdesk/entity/edit/${contextEntityId}`)}>
              {lang.EDIT_ENTITY_LABEL_EDIT}
            </Menu.Item>
            <Menu.Item
              onClick={() => navigate({ pathname: `/helpdesk/equipments`, search: `?entid=${contextEntityId}` })}
            >
              {lang.EQUIPMENT_CONSULT}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default HelpdeskEntities;
